import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { CREATE_PORTFOLIO, UPDATE_PORTFOLIO, DELETE_PORTFOLIO } from '../graphql/mutations/portfolios';
import toast from 'react-hot-toast';

const PortfolioManagementModal = ({ isOpen, onClose, defaultCreateOpen = false }) => {
  const {
    currentPortfolio,
    portfolios,
    loading,
    error,
    selectPortfolio,
    refreshPortfolios,
  } = usePortfolioContext();

  const [createName, setCreateName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [createPortfolio, { loading: creating }] = useMutation(CREATE_PORTFOLIO);
  const [updatePortfolio, { loading: updating }] = useMutation(UPDATE_PORTFOLIO);
  const [deletePortfolio, { loading: deleting }] = useMutation(DELETE_PORTFOLIO);

  useEffect(() => {
    if (!isOpen) {
      setCreateName('');
      setShowCreateForm(false);
      setEditingId(null);
      setEditingName('');
      setConfirmDeleteId(null);
    }
  }, [isOpen]);

  // When opened with defaultCreateOpen, show the create form immediately
  useEffect(() => {
    if (isOpen && defaultCreateOpen) {
      setShowCreateForm(true);
    }
  }, [isOpen, defaultCreateOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      window.addEventListener('keydown', handler);
    }
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Prevent background scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev || 'unset'; };
  }, [isOpen]);

  const sortedPortfolios = useMemo(() => {
    return [...(portfolios || [])].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [portfolios]);

  const handleQuickSelect = (p) => {
    selectPortfolio(p);
    onClose?.();
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) return;
    try {
      const { data } = await createPortfolio({ variables: { name } });
      if (data?.createPortfolio?.success) {
        toast.success('Portfolio created');
        await refreshPortfolios();
        if (data.createPortfolio.portfolio) {
          selectPortfolio(data.createPortfolio.portfolio);
        }
        setCreateName('');
        setShowCreateForm(false);
      } else {
        toast.error(data?.createPortfolio?.error || 'Failed to create portfolio');
      }
    } catch (err) {
      toast.error('Error creating portfolio');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditingName(p.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const submitEdit = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      const { data } = await updatePortfolio({ variables: { id, name } });
      if (data?.updatePortfolio?.success) {
        toast.success('Portfolio renamed');
        await refreshPortfolios();
        cancelEdit();
      } else {
        toast.error(data?.updatePortfolio?.error || 'Failed to rename portfolio');
      }
    } catch (err) {
      toast.error('Error renaming portfolio');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const confirmDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const submitDelete = async (id) => {
    try {
      const { data } = await deletePortfolio({ variables: { id } });
      if (data?.deletePortfolio) {
        toast.success('Portfolio deleted');
        await refreshPortfolios();
        cancelDelete();
      } else {
        toast.error('Failed to delete portfolio');
      }
    } catch (err) {
      toast.error('Error deleting portfolio');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manage Portfolios</h3>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-6 flex-1 min-h-0">
          <div>
            {!showCreateForm ? (
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                New portfolio
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Portfolio name"
                  className="flex-1 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !creating && createName.trim() && handleCreate()}
                  disabled={creating}
                  autoFocus
                />
                <button
                  onClick={() => { setCreateName(''); setShowCreateForm(false); }}
                  disabled={creating}
                  className="px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !createName.trim()}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            )}
          </div>

          <div>
            {sortedPortfolios.length > 0 && (
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your portfolios</div>
            )}
            {loading && (
              <div className="animate-pulse h-10 bg-gray-100 dark:bg-gray-700 rounded" />
            )}
            {error && (
              <div className="text-sm text-red-600">Error loading portfolios</div>
            )}
            <div className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md max-h-[55vh] overflow-y-auto">
              {sortedPortfolios.map((p) => {
                const isCurrent = currentPortfolio?.id === p.id;
                const isEditing = editingId === p.id;
                const isConfirmingDelete = confirmDeleteId === p.id;
                return (
                  <div key={p.id} className={`flex items-stretch ${isCurrent ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'}`}>
                    {!isEditing && !isConfirmingDelete && (
                      <button
                        onClick={() => handleQuickSelect(p)}
                        className={`flex-1 text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${isCurrent ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium truncate">{p.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Updated {new Date(p.updatedAt).toLocaleString()} · {(p.transactionsCount ?? 0)} {((p.transactionsCount ?? 0) === 1 ? 'tx' : 'txs')} · {(p.positionsCount ?? 0)} {((p.positionsCount ?? 0) === 1 ? 'position' : 'positions')}</div>
                          </div>
                          {isCurrent && (
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                          )}
                        </div>
                      </button>
                    )}

                    {isEditing && (
                      <div className="flex-1 px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            className="flex-1 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !updating && editingName.trim() && submitEdit(p.id)}
                            autoFocus
                            disabled={updating}
                          />
                          <button onClick={cancelEdit} className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Cancel</button>
                          <button
                            onClick={() => submitEdit(p.id)}
                            disabled={updating || !editingName.trim()}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {updating ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}

                    {isConfirmingDelete && (
                      <div className="flex-1 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-700 dark:text-gray-300">Delete "{p.name}"? This cannot be undone.</div>
                          <div className="flex items-center space-x-2">
                            <button onClick={cancelDelete} className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Cancel</button>
                            <button
                              onClick={() => submitDelete(p.id)}
                              disabled={deleting}
                              className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isEditing && !isConfirmingDelete && (
                      <div className="flex items-center space-x-1 px-2">
                        <button
                          onClick={() => startEdit(p)}
                          className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Rename"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        </button>
                        {portfolios.length > 1 && (
                          <button
                            onClick={() => confirmDelete(p.id)}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {sortedPortfolios.length === 0 && (
                <div className="px-4 py-6 text-sm text-gray-600 dark:text-gray-300">No portfolios yet. Create your first one above.</div>
              )}
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
};

export default PortfolioManagementModal;


